import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Layer visibility state
export interface MapLayers {
  stations: boolean;
  bays: boolean;
  segments: boolean;
  supports: boolean;
  photos: boolean;
  trajectory: boolean;
}

// Layer metadata for UI
export interface LayerInfo {
  id: keyof MapLayers;
  label: string;
  color: string;
  icon: string;
}

export const LAYER_INFO: LayerInfo[] = [
  { id: 'stations', label: 'Stations', color: '#2563eb', icon: 'Building2' },
  { id: 'bays', label: 'Bays', color: '#16a34a', icon: 'Grid3X3' },
  { id: 'segments', label: 'Segments', color: '#dc2626', icon: 'Cable' },
  { id: 'supports', label: 'Supports', color: '#6b7280', icon: 'CircleDot' },
  { id: 'photos', label: 'Photos', color: '#f59e0b', icon: 'Camera' },
  { id: 'trajectory', label: 'Flight Path', color: '#6366f1', icon: 'Navigation' },
];

interface MapSettingsStore {
  // Layer visibility
  layers: MapLayers;
  
  // Actions
  toggleLayer: (layer: keyof MapLayers) => void;
  setLayerVisibility: (layer: keyof MapLayers, visible: boolean) => void;
  showAllLayers: () => void;
  hideAllLayers: () => void;
  
  // Selectors
  isLayerVisible: (layer: keyof MapLayers) => boolean;
  getVisibleLayerCount: () => number;
}

const DEFAULT_LAYERS: MapLayers = {
  stations: true,
  bays: true,
  segments: true,
  supports: true,
  photos: true,
  trajectory: true,
};

export const useMapSettingsStore = create<MapSettingsStore>()(
  persist(
    (set, get) => ({
      layers: { ...DEFAULT_LAYERS },

      toggleLayer: (layer) => {
        set((state) => ({
          layers: {
            ...state.layers,
            [layer]: !state.layers[layer],
          },
        }));
      },

      setLayerVisibility: (layer, visible) => {
        set((state) => ({
          layers: {
            ...state.layers,
            [layer]: visible,
          },
        }));
      },

      showAllLayers: () => {
        set({
          layers: {
            stations: true,
            bays: true,
            segments: true,
            supports: true,
            photos: true,
            trajectory: true,
          },
        });
      },

      hideAllLayers: () => {
        set({
          layers: {
            stations: false,
            bays: false,
            segments: false,
            supports: false,
            photos: false,
            trajectory: false,
          },
        });
      },

      isLayerVisible: (layer) => get().layers[layer],

      getVisibleLayerCount: () => {
        const { layers } = get();
        return Object.values(layers).filter(Boolean).length;
      },
    }),
    {
      name: 'elifly-map-settings',
    }
  )
);
